import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, map, catchError } from 'rxjs/operators';
import { User, AuthResponse, LoginCredentials, UserRole } from '../models/user.model';
import { MockDataService } from './mock-data.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;

  private readonly TOKEN_KEY = 'continental_auth_token';
  private readonly USER_KEY = 'continental_user';
  private readonly apiUrl = environment.apiUrl;
  private readonly useMockData = false; // Cambiar a true para usar datos mock

  constructor(
    private http: HttpClient,
    private mockDataService: MockDataService,
    private router: Router
  ) {
    const storedUser = this.getUserFromStorage();
    this.currentUserSubject = new BehaviorSubject<User | null>(storedUser);
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public get isAuthenticated(): boolean {
    return !!this.getToken() && !!this.currentUserValue;
  }

  public get userRole(): string | null {
    return this.currentUserValue?.cargo || null;
  }

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    // Si useMockData está activado, usar datos mock
    if (this.useMockData) {
      return this.loginWithMockData(credentials);
    }

    // Login real con API
    const endpoint = credentials.userType === 'alumno'
      ? `${this.apiUrl}/api/v1/auth/login/alumno`
      : `${this.apiUrl}/api/v1/auth/login/gestion`;

    const body = credentials.userType === 'alumno'
      ? {
          documento: credentials.username,
          contrato_id: String(credentials.password)
        }
      : {
          username: credentials.username,
          password: credentials.password
        };

    return this.http.post<AuthResponse>(endpoint, body).pipe(
      map((response: AuthResponse) => {
        // Almacenar SOLO el token (no los datos del usuario)
        localStorage.setItem(this.TOKEN_KEY, response.token);

        // Normalizar respuesta
        const normalizedResponse: AuthResponse = {
          token: response.token,
          user: response.user,
          expiresIn: response.expires_in || response.expiresIn
        };

        return normalizedResponse;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error en login:', error);
        const errorMessage = error.error?.detail || 'Error al iniciar sesión. Por favor intenta nuevamente.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  private loginWithMockData(credentials: LoginCredentials): Observable<AuthResponse> {
    // Login con datos mock (para desarrollo sin backend)
    return of(credentials).pipe(
      delay(800), // Simulate network delay
      map(() => {
        const user = this.mockDataService.getUserByCredentials(
          credentials.username,
          credentials.password,
          credentials.userType
        );

        if (!user) {
          throw new Error('Credenciales inválidas');
        }

        // Generate mock JWT token
        const mockToken = this.generateMockToken(user);
        const expiresIn = 3600; // 1 hour

        // Store in localStorage
        localStorage.setItem(this.TOKEN_KEY, mockToken);
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));

        // Update current user
        this.currentUserSubject.next(user);

        return {
          user,
          token: mockToken,
          expiresIn
        };
      })
    );
  }

  /**
   * Cargar datos actualizados del usuario desde el backend
   * Utiliza el endpoint /api/v1/auth/me que requiere JWT token
   */
  loadCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/api/v1/auth/me`).pipe(
      map((userData) => {
        // Normalizar cargo a minúsculas y validar contra UserRole
        const cargoNormalizado = (userData.cargo?.toLowerCase() || 'alumno') as UserRole;

        // Normalizar datos del usuario
        const user: User = {
          ...userData,
          nombre: userData.nombre_completo || userData.nombre || '',
          celular: userData.cel || userData.celular || '',
          id: userData.documento,
          cargo: cargoNormalizado
        };

        // Actualizar localStorage y BehaviorSubject
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
        this.currentUserSubject.next(user);

        return user;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error cargando usuario actual:', error);

        // Si el token es inválido, hacer logout
        if (error.status === 401) {
          this.logout();
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Recuperación de clave — SOLO para super_admin (David/Angie).
   * Endpoint público en /login. Response siempre opaco por seguridad —
   * si no hay match, la UI muestra el mismo mensaje que si hubiera match.
   */
  recuperarClaveSuperadmin(doc: string, email: string): Observable<{ ok: boolean; mensaje: string }> {
    return this.http.post<{ ok: boolean; mensaje: string }>(
      `${this.apiUrl}/api/v1/auth/superadmin/recuperar-clave`,
      { doc, email },
    );
  }

  logout(): void {
    // Remove from localStorage
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    // Update current user
    this.currentUserSubject.next(null);

    // Redirect to login
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private getUserFromStorage(): User | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    if (userJson) {
      try {
        return JSON.parse(userJson);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  private generateMockToken(user: User): string {
    // Generate a simple mock JWT token
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({
      sub: user.id,
      documento: user.documento,
      cargo: user.cargo,
      exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour from now
    }));
    const signature = btoa('mock-signature-' + user.id);

    return `${header}.${payload}.${signature}`;
  }

  hasRole(roles: string[]): boolean {
    const userRole = this.userRole;
    return userRole ? roles.includes(userRole) : false;
  }
}
